/*
 * Purpose: Branded block node — colored BDNA, Squadbox Mega, THRPY, SensAfffect on Mission map
 * Last modified: 2026-07-10 | Agent
 * Completeness: 98
 */
import { Node } from '@/stores/workflowStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap, Wrench, CheckCircle, Trash2, Plus, WandSparkles } from 'lucide-react';
import { useState, useRef } from 'react';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getNodeSummaryOverlay } from '@/lib/mapNodeSummary';
import { getBrandedBlockDef, getBrandedBlockStyle, isMegaBrandedBlock, brandedBuildStatus } from '@/lib/brandedBlocks';
import { getMapNodeVisualStyle } from '@/lib/mapVisuals';
import { screenToWorld, useCanvasViewport } from '@/lib/canvasViewport';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export function WorkflowNode({
  node,
  inlineFailpoint = false,
  inlineDisconnected = false,
  smartNeedsInput = false,
}: {
  node: Node;
  inlineFailpoint?: boolean;
  inlineDisconnected?: boolean;
  smartNeedsInput?: boolean;
}) {
  const {
    selectNode,
    deleteNode,
    updateNode,
    selectedNode,
    connectFromNodeId,
    beginConnect,
    cancelConnect,
    completeConnect,
    autoConfigureNode,
    recordMapUndoPoint,
  } = useWorkflowStore();
  const { viewport, containerRef } = useCanvasViewport();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const nodeRef = useRef<HTMLDivElement>(null);

  const isConnectSource = connectFromNodeId === node.id;
  const isConnectTarget = connectFromNodeId !== null && connectFromNodeId !== node.id;

  const brandedDef = node.type === 'branded' ? getBrandedBlockDef(node.subtype) : undefined;
  const defaultStyle = getMapNodeVisualStyle(node);
  const brandedStyle =
    node.type === 'branded'
      ? getBrandedBlockStyle(node.subtype, String(node.config.lockColor ?? 'teal'))
      : null;
  const style = brandedStyle ?? defaultStyle;
  const Icon = style.icon;
  const label = getNodeLabel(node);
  const { typeLabel, summary } = getNodeSummaryOverlay(node);
  const isMega = node.type === 'branded' && isMegaBrandedBlock(node.subtype);
  const buildStatus = node.type === 'branded' ? brandedBuildStatus(node.subtype, node.config) : null;
  const cardWidth = isMega ? 'w-60 sm:w-64' : 'w-48';

  const GRID_SIZE = 10;

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.stopPropagation();
    recordMapUndoPoint();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const world = screenToWorld(e.clientX, e.clientY, rect, viewport);
    setIsDragging(true);
    setDragOffset({ x: world.x - node.x, y: world.y - node.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !nodeRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const world = screenToWorld(e.clientX, e.clientY, rect, viewport);
    const newX = Math.round((world.x - dragOffset.x) / GRID_SIZE) * GRID_SIZE;
    const newY = Math.round((world.y - dragOffset.y) / GRID_SIZE) * GRID_SIZE;
    updateNode(node.id, { x: Math.max(0, newX), y: Math.max(0, newY) });
  };

  const handleMouseUp = () => setIsDragging(false);

  return (
    <div
      ref={nodeRef}
      data-workflow-node
      style={{ left: `${node.x}px`, top: `${node.y}px` }}
      className="absolute select-none"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <Card
        className={cn(
          cardWidth,
          'ab-map-glass-node p-3 ui-lift-flat relative overflow-hidden rounded-lg border shadow-sm',
          style.bg,
          style.border,
          selectedNode === node.id && 'ring-2 ring-offset-2 ring-ab-green-core/50 dark:ring-ab-green-glow/45',
          isConnectSource && 'ring-2 ring-offset-2 ring-sky-500/70',
          isConnectTarget && 'ring-2 ring-offset-1 ring-dashed ring-ab-green-core/40 cursor-crosshair',
          inlineDisconnected &&
            'ring-2 ring-offset-1 ring-orange-400/80 shadow-[0_0_18px_rgba(251,146,60,0.5)]',
          smartNeedsInput &&
            'ring-2 ring-offset-1 ring-orange-500/90 animate-pulse shadow-[0_0_24px_rgba(249,115,22,0.65)]',
          inlineFailpoint &&
            'ring-2 ring-offset-1 ring-red-500/90 animate-pulse shadow-[0_0_22px_rgba(239,68,68,0.65)]',
          isDragging && 'opacity-80',
          'cursor-move'
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (connectFromNodeId) {
            if (connectFromNodeId === node.id) {
              cancelConnect();
              toast.message('Link cancelled');
              return;
            }
            const result = completeConnect(node.id);
            if (result === 'linked') {
              toast.success('Blocks linked');
            } else if (result === 'unlinked') {
              toast.success('Link removed');
            }
            return;
          }
          selectNode(node.id);
        }}
        onMouseDown={handleMouseDown}
      >
        {brandedStyle?.stripe && (
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${brandedStyle.stripe}`} aria-hidden />
        )}

        {brandedDef && (
          <div className="absolute top-1.5 right-8 flex gap-1">
            <Badge className={`text-[9px] px-1.5 py-0 h-4 border-0 ${brandedDef.style.badgeClass}`}>
              {brandedDef.brand}
            </Badge>
            {buildStatus === 'idea' && (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-dashed">
                Idea
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-start gap-2">
          <Icon className={cn('w-5 h-5 shrink-0 mt-0.5', style.iconClass)} />
          <div className="flex-1 min-w-0 pr-6">
            <p className="font-medium text-sm text-foreground truncate">{label}</p>
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/90 truncate mt-0.5">
              {typeLabel}
            </p>
            <p
              className="text-xs text-muted-foreground mt-1.5 line-clamp-2 leading-snug"
              title={summary}
            >
              {summary}
            </p>
            {brandedDef && (
              <p className="text-[10px] text-muted-foreground truncate mt-1">{brandedDef.shortLabel}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 absolute top-2 right-1"
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(node.id);
            }}
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>

        {isMega && (
          <p className="text-[10px] text-muted-foreground mt-2 pl-0.5 line-clamp-2">
            Mega block — squad orchestration via contextual configuration
          </p>
        )}

        <div className="flex gap-1 mt-2 pt-2 border-t border-border/25">
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 flex-1"
            title="Smart Blocks — auto fix/configure this block"
            onClick={(e) => {
              e.stopPropagation();
              const changes = autoConfigureNode(node.id);
              if (changes.length > 0) {
                toast.success('Smart Blocks configured this block');
              } else {
                toast.message('Block already configured');
              }
            }}
          >
            <WandSparkles className="w-3 h-3 text-muted-foreground" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 flex-1"
            title={
              isConnectSource
                ? 'Cancel (click this block again)'
                : 'Link or unlink — click + then the other block'
            }
            aria-pressed={isConnectSource}
            onClick={(e) => {
              e.stopPropagation();
              if (isConnectSource) {
                cancelConnect();
                toast.message('Link cancelled');
                return;
              }
              beginConnect(node.id);
              toast.message('Click another block to link or unlink');
            }}
          >
            <Plus className={`w-3 h-3 ${isConnectSource ? 'text-sky-600' : 'text-muted-foreground'}`} />
          </Button>
        </div>
      </Card>
    </div>
  );
}
